import { createI18n } from 'vue-i18n';
import en from '@site/locales/en.json';
import es from '@site/locales/es.json';
import pt from '@site/locales/pt.json';

const SUPPORTED_LOCALES = ['en', 'es', 'pt'] as const;
type SupportedLocale = typeof SUPPORTED_LOCALES[number];
export const DEFAULT_LOCALE: SupportedLocale = 'es';

// Load messages
export const messages = {
  en,
  es,
  pt,
};

function isSupportedLocale(locale: string): locale is SupportedLocale {
  return SUPPORTED_LOCALES.includes(locale as SupportedLocale);
}

/**
 * Detect locale from browser
 */
function detectBrowserLocale(): SupportedLocale {
  if (typeof navigator === 'undefined' || !navigator.language) {
    return DEFAULT_LOCALE;
  }

  const browserLang = navigator.language;
  const baseLang = browserLang.split('-')[0].toLowerCase();

  if (isSupportedLocale(baseLang)) {
    return baseLang;
  }

  return DEFAULT_LOCALE;
}

/**
 * Get locale from localStorage
 */
function getStoredLocale(): SupportedLocale | null {
  if (typeof window === 'undefined') return null;

  try {
    const stored = localStorage.getItem('user-locale');
    return stored && isSupportedLocale(stored) ? stored : null;
  } catch {
    return null;
  }
}

/**
 * Store locale in localStorage
 */
export function storeLocale(locale: SupportedLocale): void {
  if (typeof window === 'undefined') return;

  try {
    localStorage.setItem('user-locale', locale);
  } catch {
    // Ignore storage errors
  }
}

/**
 * Resolve initial locale based on user state
 *
 * Escenario A - Usuario anónimo:
 *   1) localStorage
 *   2) navigator.language
 *   3) default 'es'
 *
 * Escenario B - Usuario autenticado sin locale:
 *   Usa lo mismo que anónimo (se persistirá post-login)
 *
 * Escenario C - Usuario autenticado con locale:
 *   Usa user.locale
 */
export function resolveInitialLocale(userLocale: string | null | undefined): SupportedLocale {
  if (userLocale && isSupportedLocale(userLocale)) {
    return userLocale;
  }

  const stored = getStoredLocale();
  if (stored) {
    return stored;
  }

  return detectBrowserLocale();
}

// Create i18n instance
export const i18n = createI18n({
  legacy: false,
  locale: resolveInitialLocale(null),
  fallbackLocale: DEFAULT_LOCALE,
  messages,
  globalInjection: true,
});

// Initialize locale (to be called after auth state is known)
export function initializeLocale(userLocale: string | null | undefined): SupportedLocale {
  const locale = resolveInitialLocale(userLocale);
  i18n.global.locale.value = locale;
  storeLocale(locale);
  return locale;
}

export default i18n;

export type { SupportedLocale };
